import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  Modal,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { SERVER_BASE_URL } from "./Config";

interface DeliveryFormProps {
  patientId: number | null;
  onDeliveryInfoChange: (deliveryInfo: any) => void;
  supportsBatching?: boolean; // New prop to restrict delivery methods for batching
  patientAddresses?: any[]; // Optional prop to pass addresses from parent
  initialDeliveryMethod?: string;
  initialBillingOption?: string;
  initialSpecialInstructions?: string;
  initialPatientAddressId?: number;
  initialProviderAddressId?: number;
}

const DeliveryForm: React.FC<DeliveryFormProps> = ({
  patientId,
  onDeliveryInfoChange,
  supportsBatching = false,
  patientAddresses: providedPatientAddresses,
  initialDeliveryMethod,
  initialBillingOption,
  initialSpecialInstructions,
  initialPatientAddressId,
  initialProviderAddressId,
}) => {
  // Function to get minimum selectable date based on 2pm Eastern cutoff
  const getMinimumSelectableDate = () => {
    // Get current Eastern time using Intl API (handles EST/EDT automatically)
    const eastern = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
    const easternHour = eastern.getHours();

    // Before 2pm Eastern: 1 day out. After 2pm: 2 days out.
    const daysToAdd = easternHour < 14 ? 1 : 2;

    const minDate = new Date(eastern);
    minDate.setDate(minDate.getDate() + daysToAdd);

    // If we land on a weekend, push to Monday
    const day = minDate.getDay();
    if (day === 0) minDate.setDate(minDate.getDate() + 1); // Sunday -> Monday
    if (day === 6) minDate.setDate(minDate.getDate() + 2); // Saturday -> Monday

    const yyyy = minDate.getFullYear();
    const mm = String(minDate.getMonth() + 1).padStart(2, '0');
    const dd = String(minDate.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Function to get default need by date (minimum selectable date)
  const getDefaultNeedByDate = () => {
    return getMinimumSelectableDate();
  };

  const [deliveryInfo, setDeliveryInfo] = useState<{
    deliveryMethod: string;
    pickupAddressId: number | null;
    providerAddressId: number | null;
    patientAddressId: number | null;
    addressString: string;
    needByDate: string;
    isAsap: boolean;
    billingOption: string;
    specialInstructions: string;
  }>({
    deliveryMethod: initialDeliveryMethod || "pickup",
    pickupAddressId: null,
    providerAddressId: null,
    patientAddressId: null,
    addressString: "",
    needByDate: getDefaultNeedByDate(),
    isAsap: false,
    billingOption: initialBillingOption || "patientPays",
    specialInstructions: initialSpecialInstructions || "",
  });

  const [pickupLocations] = useState([
    {
      id: 1,
      name: "The Chemist Shop: 30-15 38th Ave, Long Island City, NY 11101",
    },
  ]);

  const [patientAddresses, setPatientAddresses] = useState<any[]>([]);
  const [providerAddresses, setProviderAddresses] = useState<any[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<any>(null);
  const [fetchingAddresses, setFetchingAddresses] = useState(false);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({ street: "", city: "", state: "", zip: "", isDefault: false });
  const [savingAddress, setSavingAddress] = useState(false);

  // Use provided addresses if available
  useEffect(() => {
    if (providedPatientAddresses) {
      setPatientAddresses(providedPatientAddresses);
    }
  }, [providedPatientAddresses]);

  // Validate selected address when patient addresses change
  useEffect(() => {
    if (deliveryInfo.deliveryMethod === "shipToPatient" && patientAddresses.length > 0) {
      // Check if the currently selected address is still valid
      const isSelectedAddressValid = patientAddresses.some(addr => addr.id === deliveryInfo.patientAddressId);

      if (!isSelectedAddressValid) {
        // Current selection is not valid for this patient, so fall back to the reorder
        // address, then the patient's default.
        const targetAddress = pickAddress(patientAddresses, null, initialPatientAddressId);
        if (targetAddress) {
          const updatedDeliveryInfo = {
            ...deliveryInfo,
            patientAddressId: targetAddress.id,
            addressString: formatAddressString({
              ...deliveryInfo,
              patientAddressId: targetAddress.id,
            }),
          };
          setDeliveryInfo(updatedDeliveryInfo);
          setSelectedAddressId(targetAddress.id);
          onDeliveryInfoChange(updatedDeliveryInfo);
        }
      }
    } else if (deliveryInfo.deliveryMethod === "shipToPatient" && patientAddresses.length === 0) {
      // No addresses available, clear selection
      const updatedDeliveryInfo = {
        ...deliveryInfo,
        patientAddressId: null,
        addressString: "",
      };
      setDeliveryInfo(updatedDeliveryInfo);
      setSelectedAddressId(null);
      onDeliveryInfoChange(updatedDeliveryInfo);
    }
  }, [patientAddresses]);

  useEffect(() => {
    if (deliveryInfo.deliveryMethod === "shipToPatient") {
      // Only fetch if addresses weren't provided
      if (!providedPatientAddresses) {
        fetchPatientAddresses();
      }
    } else if (deliveryInfo.deliveryMethod === "shipToProvider") {
      fetchProviderAddresses();
    } else if (deliveryInfo.deliveryMethod === "pickup") {
      if (!deliveryInfo.pickupAddressId && pickupLocations.length > 0) {
        const firstPickup = pickupLocations[0];
        const updated = {
          ...deliveryInfo,
          pickupAddressId: firstPickup.id,
          addressString: firstPickup.name,
        };
        setDeliveryInfo(updated);
        setSelectedAddressId(firstPickup.id);
        onDeliveryInfoChange(updated);
      } else {
        setSelectedAddressId(deliveryInfo.pickupAddressId);
      }
    }
  }, [deliveryInfo.deliveryMethod, providedPatientAddresses]);

  useEffect(() => {
    if (
      fetchingAddresses &&
      (patientAddresses.length > 0 || providerAddresses.length > 0)
    ) {
      handleFirstAddressSelection();
      setFetchingAddresses(false);
    }
  }, [patientAddresses, providerAddresses, fetchingAddresses]);

  const fetchPatientAddresses = async () => {
    setFetchingAddresses(true);
    try {
      const response = await fetch(
        `${SERVER_BASE_URL}/patient_address/${patientId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("providerToken")}`,
          },
        },
      );
      const data = await response.json();
      setPatientAddresses(data);
    } catch (error) {
      console.error("Failed to fetch patient addresses:", error);
      setFetchingAddresses(false);
    }
  };

  const fetchProviderAddresses = async () => {
    setFetchingAddresses(true);
    try {
      const response = await fetch(`${SERVER_BASE_URL}/provider_address`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("providerToken")}`,
        },
      });
      const data = await response.json();
      setProviderAddresses(data);
    } catch (error) {
      console.error("Failed to fetch provider addresses:", error);
      setFetchingAddresses(false);
    }
  };

  const handleFirstAddressSelection = () => {
    let initialAddressId: number | null = null;
    let updatedDeliveryInfo = { ...deliveryInfo };

    if (
      deliveryInfo.deliveryMethod === "shipToPatient" &&
      patientAddresses.length > 0
    ) {
      initialAddressId = pickAddress(
        patientAddresses,
        deliveryInfo.patientAddressId,
        initialPatientAddressId,
      )["id"];
      updatedDeliveryInfo = {
        ...updatedDeliveryInfo,
        patientAddressId: initialAddressId,
        addressString: formatAddressString({
          ...updatedDeliveryInfo,
          patientAddressId: initialAddressId,
        }),
      };
      setDeliveryInfo(updatedDeliveryInfo);
    } else if (
      deliveryInfo.deliveryMethod === "shipToProvider" &&
      providerAddresses.length > 0
    ) {
      initialAddressId = pickAddress(
        providerAddresses,
        deliveryInfo.providerAddressId,
        initialProviderAddressId,
      )["id"];
      updatedDeliveryInfo = {
        ...updatedDeliveryInfo,
        providerAddressId: initialAddressId,
        addressString: formatAddressString({
          ...updatedDeliveryInfo,
          providerAddressId: initialAddressId,
        }),
      };
      setDeliveryInfo(updatedDeliveryInfo);
    }

    setSelectedAddressId(initialAddressId);
    onDeliveryInfoChange(updatedDeliveryInfo);
  };

  // Which address to auto-select: keep a still-valid current selection, then the address
  // carried over from a reorder, then the default, then the first one. Never rely on the
  // list's order alone - the default is not guaranteed to be first.
  const pickAddress = (addresses: any[], currentId: any, preferredId: any) => {
    const byId = (id: any) => (id ? addresses.find((a: any) => a.id === id) : undefined);
    return byId(currentId) || byId(preferredId) || addresses.find((a: any) => a.isDefault) || addresses[0];
  };

  const formatAddressString = (updatedDeliveryInfo: any) => {
    let formattedAddress = "";
    if (updatedDeliveryInfo.deliveryMethod === "pickup") {
      const location = pickupLocations.find(
        (location) => location.id === updatedDeliveryInfo.pickupAddressId,
      );
      formattedAddress = location ? location.name : "";
    } else if (updatedDeliveryInfo.deliveryMethod === "shipToPatient") {
      const address = patientAddresses.find(
        (address) => address["id"] === updatedDeliveryInfo.patientAddressId,
      );
      if (address) {
        // @ts-ignore
        formattedAddress = `${address.street}, ${address.city}, ${address.state} ${address.zip}`;
      }
    } else if (updatedDeliveryInfo.deliveryMethod === "shipToProvider") {
      const address = providerAddresses.find(
        (address) => address["id"] === updatedDeliveryInfo.providerAddressId,
      );
      if (address) {
        // @ts-ignore
        formattedAddress = `${address.street}, ${address.city}, ${address.state} ${address.zip}`;
      }
    }
    return formattedAddress;
  };

  const handleDeliveryMethodChange = (event: any) => {
    const selectedMethod = event.target.value;
    let initialAddressId = null;
    let updatedDeliveryInfo = {
      ...deliveryInfo,
      deliveryMethod: selectedMethod,
    };

    if (selectedMethod === "pickup") {
      initialAddressId =
        pickupLocations.length > 0 ? pickupLocations[0].id : null;
      // @ts-ignore
      updatedDeliveryInfo.pickupAddressId = initialAddressId;
      updatedDeliveryInfo.providerAddressId = null;
      updatedDeliveryInfo.patientAddressId = null;
    } else if (selectedMethod === "shipToPatient") {
      if (patientAddresses.length > 0) {
        initialAddressId = patientAddresses[0]["id"];
        updatedDeliveryInfo.patientAddressId = initialAddressId;
      }
      updatedDeliveryInfo.pickupAddressId = null;
      updatedDeliveryInfo.providerAddressId = null;
    } else if (selectedMethod === "shipToProvider") {
      if (providerAddresses.length > 0) {
        initialAddressId = providerAddresses[0]["id"];
        updatedDeliveryInfo.providerAddressId = initialAddressId;
      }
      updatedDeliveryInfo.pickupAddressId = null;
      updatedDeliveryInfo.patientAddressId = null;
    }

    updatedDeliveryInfo.addressString =
      formatAddressString(updatedDeliveryInfo);

    setDeliveryInfo(updatedDeliveryInfo);
    setSelectedAddressId(initialAddressId);
    onDeliveryInfoChange(updatedDeliveryInfo);
  };

  const handleAddressSelect = (event: any) => {
    const selectedAddressId = event.target.value;
    let updatedDeliveryInfo = { ...deliveryInfo };
    if (deliveryInfo.deliveryMethod === "pickup") {
      updatedDeliveryInfo.pickupAddressId = selectedAddressId;
    } else if (deliveryInfo.deliveryMethod === "shipToPatient") {
      updatedDeliveryInfo.patientAddressId = selectedAddressId;
    } else if (deliveryInfo.deliveryMethod === "shipToProvider") {
      updatedDeliveryInfo.providerAddressId = selectedAddressId;
    }
    updatedDeliveryInfo.addressString =
      formatAddressString(updatedDeliveryInfo);

    setDeliveryInfo(updatedDeliveryInfo);
    setSelectedAddressId(selectedAddressId);
    onDeliveryInfoChange(updatedDeliveryInfo);
  };

  const handleSaveNewAddress = async () => {
    setSavingAddress(true);
    try {
      const response = await fetch(`${SERVER_BASE_URL}/provider_address`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("providerToken")}`,
        },
        body: JSON.stringify({
          street: newAddress.street,
          city: newAddress.city,
          state: newAddress.state,
          zip: newAddress.zip,
          makeDefault: newAddress.isDefault || providerAddresses.length === 0,
        }),
      });
      const data = await response.json();

      // Re-fetch addresses
      const addrResponse = await fetch(`${SERVER_BASE_URL}/provider_address`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("providerToken")}`,
        },
      });
      const addresses = await addrResponse.json();
      setProviderAddresses(addresses);

      // Auto-select the new address
      const newId = data.address_id;
      const updatedDeliveryInfo = {
        ...deliveryInfo,
        providerAddressId: newId,
        addressString: `${newAddress.street}, ${newAddress.city}, ${newAddress.state} ${newAddress.zip}`,
      };
      setDeliveryInfo(updatedDeliveryInfo);
      setSelectedAddressId(newId);
      onDeliveryInfoChange(updatedDeliveryInfo);

      // Reset form
      setShowNewAddressForm(false);
      setNewAddress({ street: "", city: "", state: "", zip: "", isDefault: false });
    } catch (error) {
      console.error("Failed to save address:", error);
    } finally {
      setSavingAddress(false);
    }
  };

  const handleCancelNewAddress = () => {
    setShowNewAddressForm(false);
    setNewAddress({ street: "", city: "", state: "", zip: "", isDefault: false });
  };

  const handleInputChange = (event: any) => {
    const { name, value } = event.target;
    
    // Prevent setting dates before the minimum selectable date or on weekends
    if (name === 'needByDate') {
      const minSelectableDate = getMinimumSelectableDate();
      if (value < minSelectableDate) {
        return;
      }
      const selectedDay = new Date(value + 'T12:00:00').getDay();
      if (selectedDay === 0 || selectedDay === 6) {
        return;
      }
    }
    
    const updatedDeliveryInfo = { ...deliveryInfo, [name]: value };
    setDeliveryInfo(updatedDeliveryInfo);
    onDeliveryInfoChange(updatedDeliveryInfo);
  };

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel shrink>Delivery Method</InputLabel>
        <Select
          value={deliveryInfo.deliveryMethod}
          onChange={handleDeliveryMethodChange}
          label="Delivery Method"
          // Ensure that InputLabelProps includes shrink to prevent overlay issues
          inputProps={{
            "aria-label": "Delivery Method",
            shrink: true,
          }}
        >
          <MenuItem value="pickup">
            <Typography align="left" sx={{ width: "100%" }}>
              Pickup
            </Typography>
          </MenuItem>
          <MenuItem value="shipToProvider">
            <Typography align="left" sx={{ width: "100%" }}>
              Ship to Provider
            </Typography>
          </MenuItem>
          <MenuItem value="shipToPatient">
            <Typography align="left" sx={{ width: "100%" }}>
              Ship to Patient
            </Typography>
          </MenuItem>
        </Select>
      </FormControl>

      {deliveryInfo.deliveryMethod === "pickup" && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel shrink>Pickup Location</InputLabel>
          <Select
            value={selectedAddressId}
            onChange={handleAddressSelect}
            label="Pickup Location"
            displayEmpty
            inputProps={{
              "aria-label": "Pickup Location",
            }}
            sx={{
              '& .MuiSelect-select': {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textAlign: 'left',
              }
            }}
          >
            {/* Optional placeholder if needed */}
            <MenuItem value="" disabled>
              <Typography align="left" sx={{ width: "100%" }}>
                Select Pickup Location
              </Typography>
            </MenuItem>
            {pickupLocations.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                {location.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {(deliveryInfo.deliveryMethod === "shipToPatient" ||
        deliveryInfo.deliveryMethod === "shipToProvider") && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel shrink>Delivery Location</InputLabel>{" "}
          {/* Ensures consistent label positioning */}
          <Select
            value={selectedAddressId || ""}
            onChange={handleAddressSelect}
            label="Delivery Location"
            displayEmpty
            inputProps={{
              "aria-label": "Delivery Location",
            }}
            sx={{
              '& .MuiSelect-select': {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textAlign: 'left',
              }
            }}
          >
            {/* Optional placeholder if needed */}
            <MenuItem value="" disabled>
              <Typography align="left" sx={{ width: "100%" }}>
                Select Delivery Location
              </Typography>
            </MenuItem>
            {(deliveryInfo.deliveryMethod === "shipToPatient"
              ? patientAddresses
              : providerAddresses
            ).map((address: any) => (
              <MenuItem key={address.id} value={address.id}>
                <Typography align="left" sx={{ width: "100%" }}>
                  {`${address.street}, ${address.city}, ${address.state} ${address.zip}`}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {deliveryInfo.deliveryMethod === "shipToProvider" && (
        <Button
          variant="text"
          onClick={() => {
            setNewAddress({ street: "", city: "", state: "", zip: "", isDefault: providerAddresses.length === 0 });
            setShowNewAddressForm(true);
          }}
          sx={{ mb: 2, textTransform: "none" }}
        >
          + Add New Address
        </Button>
      )}

      <Modal
        open={showNewAddressForm}
        onClose={handleCancelNewAddress}
        aria-labelledby="new-address-modal"
        sx={{ zIndex: 1500 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 400 },
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: { xs: 2, sm: 4 },
          }}
        >
          <IconButton
            onClick={handleCancelNewAddress}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" component="h2" gutterBottom>
            Add New Address
          </Typography>
          <TextField
            label="Street"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={newAddress.street}
            onChange={(e) => setNewAddress({ ...newAddress, street: e.target.value })}
          />
          <TextField
            label="City"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={newAddress.city}
            onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
          />
          <TextField
            label="State"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={newAddress.state}
            onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
          />
          <TextField
            label="ZIP"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={newAddress.zip}
            onChange={(e) => setNewAddress({ ...newAddress, zip: e.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newAddress.isDefault}
                onChange={(e) => setNewAddress({ ...newAddress, isDefault: e.target.checked })}
              />
            }
            label="Make this the default address"
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveNewAddress}
            disabled={savingAddress}
          >
            {savingAddress ? <CircularProgress size={20} /> : "Save Address"}
          </Button>
        </Box>
      </Modal>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <TextField
          label="Need By Date"
          type="date"
          name="needByDate"
          value={deliveryInfo.needByDate}
          onChange={handleInputChange}
          sx={{ flex: 1 }}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: getMinimumSelectableDate()
          }}
        />
      </Box>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel shrink>Billing Option</InputLabel>
        <Select
          name="billingOption"
          value={deliveryInfo.billingOption}
          onChange={handleInputChange}
          label="Billing Option"
          displayEmpty
          inputProps={{
            "aria-label": "Billing Option",
          }}
        >
          <MenuItem value="patientPays">
            <Typography align="left" sx={{ width: "100%" }}>
              Patient Pays
            </Typography>
          </MenuItem>
          <MenuItem value="doctorPays">
            <Typography align="left" sx={{ width: "100%" }}>
              Doctor Pays
            </Typography>
          </MenuItem>
        </Select>
      </FormControl>

    </Box>
  );
};

export default DeliveryForm;
